import React, { useState, useMemo } from 'react';
import { CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, X, Info } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getFormTitle, shouldShowVehicleInfo } from './FormUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { useInterestDataSync } from '@/hooks/useInterestData';
import { useIsMobile } from '@/hooks/use-mobile';
import { buildOfferFormTitle, getVehicleYmmt } from '@/utils/vehicleContextHelpers';

interface FormHeaderProps {
  formType: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose: () => void;
  formData?: { [key: string]: any };
  title?: string; // Allow explicit title override
}

const FormHeader: React.FC<FormHeaderProps> = ({
  formType,
  collapsed,
  onToggleCollapse,
  onClose,
  formData,
  title: explicitTitle,
}) => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const storedInterest = useInterestDataSync();

  // Title priority: Explicit prop -> Form.Title -> Offer-specific YMMT default -> Default (memoized)
  const title = useMemo(() => {
    const aiTitle = formData?.Title || formData?.Form?.Title;

    let defaultTitle = getFormTitle(formType, t);
    if (formType === 'Offer' && !explicitTitle && !aiTitle) {
      const ymmt = getVehicleYmmt(storedInterest?.vehicle);
      defaultTitle = buildOfferFormTitle(language === 'fr' ? 'fr' : 'en', ymmt);
    }

    const finalTitle = explicitTitle || aiTitle || defaultTitle;
    return finalTitle;
  }, [explicitTitle, formData?.Title, formData?.Form?.Title, formType, t, language, storedInterest?.vehicle]);
  const showTooltip = shouldShowVehicleInfo(formType);

  const isValidValue = (value: any): boolean => {
    if (!value) return false;
    const stringValue = String(value).trim();
    return stringValue.length > 0 && 
           stringValue.toLowerCase() !== 'not available' && 
           stringValue.toLowerCase() !== 'not specified';
  };

  const renderVehicleTooltip = () => {
    const vehicleInfo = storedInterest?.vehicle;
    if (!vehicleInfo) return null;

    // Check if any fields have valid values
    const fields = ['year', 'make', 'model', 'version', 'color', 'stock', 'vin'];
    const hasValidFields = fields.some(field => isValidValue(vehicleInfo[field]));
    
    if (!hasValidFields) return null;

    return (
      <div className="space-y-1 text-xs w-full">
        <div className="font-semibold text-white bg-gray-800 px-3 py-2 rounded-t-md text-center text-sm mb-3 -mx-2 -mt-2">
          Vehicle of Interest
        </div>
        <div className="flex gap-3">
          {vehicleInfo.main_picture && (
            <div className="flex-shrink-0">
              <img 
                src={vehicleInfo.main_picture} 
                alt="Vehicle" 
                className="w-32 h-24 object-cover rounded d2cbot-bot_border"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
           <div className="grid grid-cols-1 gap-1 flex-1">
            {vehicleInfo.year && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">Year:</span>
                <span className="font-medium text-xs">{vehicleInfo.year}</span>
              </div>
            )}
            {vehicleInfo.make && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">Make:</span>
                <span className="font-medium text-xs">{vehicleInfo.make}</span>
              </div>
            )}
            {vehicleInfo.model && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">Model:</span>
                <span className="font-medium text-xs">{vehicleInfo.model}</span>
              </div>
            )}
            {vehicleInfo.version && (
              <div className="flex justify-between items-start">
                <span className="text-gray-600 flex-shrink-0 text-xs">Trim:</span>
                <span className="font-medium text-xs break-words text-wrap leading-tight text-right">{vehicleInfo.version}</span>
              </div>
            )}
            {isValidValue(vehicleInfo.color) && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">Color:</span>
                <span className="font-medium text-xs">{vehicleInfo.color}</span>
              </div>
            )}
            {isValidValue(vehicleInfo.stock) && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">Stock #:</span>
                <span className="font-mono text-xs font-medium text-right">{vehicleInfo.stock}</span>
              </div>
            )}
            {isValidValue(vehicleInfo.vin || vehicleInfo.VIN) && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-xs">VIN:</span>
                <span className="font-mono text-xs font-medium text-right">{vehicleInfo.vin || vehicleInfo.VIN}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const headerContent = (
    <CardHeader 
      className={`flex flex-row items-center justify-between px-3 py-0 space-y-0 h-8 min-h-8 ${
        formType === 'Offer' ? 'bg-red-600' : 'bg-gray-800'
      } d2cbot-bot_border-b ${collapsed ? 'cursor-pointer' : ''}`}
      onClick={collapsed ? onToggleCollapse : undefined}
    >
      <div className="flex items-center space-x-1">
        <CardTitle className={`text-[13px] font-medium leading-none ${
          formType === 'Offer' 
            ? 'text-white animate-[pulse_2.6s_cubic-bezier(0.4,0,0.6,1)_infinite] drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]' 
            : 'text-white'
        }`}>
          {title}
        </CardTitle>
      </div>
      <div className="flex items-center space-x-1">
        {!collapsed && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className="hover:bg-white/20 text-white [&_svg]:text-white h-6 w-6 p-0"
          >
            <ChevronDown className="h-3 w-3 text-white" />
            <span className="sr-only">Collapse</span>
          </Button>
        )}
        {collapsed && (
          <ChevronUp className="h-3 w-3 text-white" />
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="hover:bg-white/20 text-white h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </CardHeader>
  );

  if (!showTooltip) {
    return headerContent;
  }

  // Mobile: use dialog
  if (isMobile) {
    return (
      <>
        {headerContent}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="w-[95vw] max-w-md mx-auto p-0 gap-0">
            <div className="p-4">
              {renderVehicleTooltip()}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop: use tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {headerContent}
        </TooltipTrigger>
        <TooltipContent 
          className="bg-white d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-lg p-2 w-[420px] z-[999999]" 
          side="top" 
          sideOffset={10}
          align="center"
          avoidCollisions={false}
        >
          {renderVehicleTooltip()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FormHeader;